import type { MpUserSelfWecomPush } from '../lib/mpUserSelfPublish';
import {
  mpUserWecomDefaultProfileTemplate,
  mpUserWecomDefaultPublishedTemplate,
  mpUserWecomDefaultReviewTemplate,
} from '../lib/mpUserSelfPublish';
import { Button } from './ui/Button';
import { inputClass, labelClass } from './ui/inputStyles';

type Props = {
  settings: MpUserSelfWecomPush;
  onChange: (patch: Partial<MpUserSelfWecomPush>) => void;
};

const REVIEW_PLACEHOLDERS =
  '{minipro_name} {site_name} {title} {publisher_nickname} {action_label} {entry_id} {time} {review_url}';
const PUBLISHED_PLACEHOLDERS =
  '{minipro_name} {site_name} {title} {publisher_nickname} {action_label} {entry_id} {time} {body_excerpt} {entry_url}';
const PROFILE_PLACEHOLDERS = '{minipro_name} {publisher_nickname} {pending_nickname} {time} {users_url}';

export function MiniprogramWecomPushCard({ settings, onChange }: Props) {
  const textareaClass = inputClass + ' resize-y font-mono text-xs';
  const msgtype = settings.msgtype === 'markdown' ? 'markdown' : 'text';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-1 text-base font-semibold text-slate-900">企业微信群推送</h3>
      <p className="mb-4 text-sm text-slate-600">
        用户自助发布时向企业微信群机器人推送提醒：进入<strong className="font-medium text-slate-800">待审核</strong>
        与<strong className="font-medium text-slate-800">发布成功（已上线）</strong>
        两种场景均支持（与站点「接入与推送」金数据链路独立，按本小程序单独配置）。关闭审核时，用户发布后将直接触发「发布成功」推送。
      </p>
      <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3 hover:bg-slate-50">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
          checked={!!settings.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-slate-800">启用企微群推送</span>
          <span className="mt-1 block text-xs leading-relaxed text-slate-500">
            开启后填写 Webhook 地址；下方模板已预填默认值，可按需修改。
          </span>
        </span>
      </label>
      <div className="space-y-4">
        <div>
          <label className={labelClass} htmlFor="mp-wecom-url">
            Webhook 地址
          </label>
          <input
            id="mp-wecom-url"
            className={inputClass}
            value={settings.webhook_url}
            onChange={(e) => onChange({ webhook_url: e.target.value })}
            placeholder="https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=..."
            disabled={!settings.enabled}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="mp-wecom-msgtype">
            消息格式
          </label>
          <select
            id="mp-wecom-msgtype"
            className={inputClass}
            value={settings.msgtype}
            onChange={(e) => onChange({ msgtype: e.target.value === 'markdown' ? 'markdown' : 'text' })}
            disabled={!settings.enabled}
          >
            <option value="text">纯文本</option>
            <option value="markdown">Markdown</option>
          </select>
          <p className="mt-1 text-xs text-slate-500">切换格式后，可点各模板下的「恢复默认」生成对应格式模板。</p>
        </div>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3 hover:bg-slate-50">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            checked={!!settings.on_entry_review}
            onChange={(e) => onChange({ on_entry_review: e.target.checked })}
            disabled={!settings.enabled}
          />
          <span className="min-w-0 flex-1 text-sm text-slate-800">待审核时推送（开启审核或命中敏感词等进入待审）</span>
        </label>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3 hover:bg-slate-50">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            checked={!!settings.on_entry_published}
            onChange={(e) => onChange({ on_entry_published: e.target.checked })}
            disabled={!settings.enabled}
          />
          <span className="min-w-0 flex-1 text-sm text-slate-800">
            发布成功时推送（未开审核直接上线、修改直接生效、或管理员审核通过后）
          </span>
        </label>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3 hover:bg-slate-50">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            checked={!!settings.on_profile_review}
            onChange={(e) => onChange({ on_profile_review: e.target.checked })}
            disabled={!settings.enabled}
          />
          <span className="min-w-0 flex-1 text-sm text-slate-800">用户昵称/头像进入待审时推送</span>
        </label>
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <label className={labelClass + ' mb-0'} htmlFor="mp-wecom-entry-tpl">
              待审核消息模板
            </label>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={!settings.enabled}
              onClick={() => onChange({ body_template: mpUserWecomDefaultReviewTemplate(msgtype) })}
            >
              恢复默认
            </Button>
          </div>
          <p className="mb-2 text-xs text-slate-500">
            占位符：{REVIEW_PLACEHOLDERS}。{'{review_url}'} 指向站长手机端{' '}
            <code className="rounded bg-slate-100 px-1">/w/entries</code>，可直接审核。
          </p>
          <textarea
            id="mp-wecom-entry-tpl"
            className={textareaClass}
            rows={8}
            value={settings.body_template}
            onChange={(e) => onChange({ body_template: e.target.value })}
            disabled={!settings.enabled}
          />
        </div>
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <label className={labelClass + ' mb-0'} htmlFor="mp-wecom-published-tpl">
              发布成功消息模板
            </label>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={!settings.enabled}
              onClick={() => onChange({ published_body_template: mpUserWecomDefaultPublishedTemplate(msgtype) })}
            >
              恢复默认
            </Button>
          </div>
          <p className="mb-2 text-xs text-slate-500">
            占位符：{PUBLISHED_PLACEHOLDERS}。{'{entry_url}'} 同样指向站长手机端信息详情。
          </p>
          <textarea
            id="mp-wecom-published-tpl"
            className={textareaClass}
            rows={9}
            value={settings.published_body_template}
            onChange={(e) => onChange({ published_body_template: e.target.value })}
            disabled={!settings.enabled}
          />
        </div>
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <label className={labelClass + ' mb-0'} htmlFor="mp-wecom-profile-tpl">
              资料待审消息模板
            </label>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={!settings.enabled}
              onClick={() => onChange({ profile_body_template: mpUserWecomDefaultProfileTemplate(msgtype) })}
            >
              恢复默认
            </Button>
          </div>
          <p className="mb-2 text-xs text-slate-500">占位符：{PROFILE_PLACEHOLDERS}</p>
          <textarea
            id="mp-wecom-profile-tpl"
            className={textareaClass}
            rows={7}
            value={settings.profile_body_template}
            onChange={(e) => onChange({ profile_body_template: e.target.value })}
            disabled={!settings.enabled}
          />
        </div>
      </div>
    </div>
  );
}
